/**
 * Trauma / stress response wheel data.
 *
 * Self-reflection only. These are automatic protective patterns of the nervous
 * system — never presented as a diagnosis, a personality type or a fixed trait.
 * People commonly move between several responses depending on the situation.
 */

import { PRACTICE_LIBRARY } from "@/data/article-practice";

export type TraumaResponse = {
  key: string;
  emoji: string;
  /** Short wheel label. */
  label: string;
  /** Full name used in headings. */
  title: string;
  /** First-person sentence describing the protective intention. */
  voice: string;
  color: string;
  /** Plain-language explanation of what the nervous system is doing. */
  whatIsHappening: string;
  /** Signs shown as chips on the wheel panel. */
  signs: readonly string[];
  body: readonly string[];
  thoughts: readonly string[];
  behaviour: readonly string[];
  triggers: readonly string[];
  rightNow: readonly string[];
  /** Keys into PRACTICE_LIBRARY — at least three, matched to the response. */
  practices: readonly (keyof typeof PRACTICE_LIBRARY)[];
};

export const TRAUMA_RESPONSES: readonly TraumaResponse[] = [
  {
    key: "fight",
    emoji: "🥊",
    label: "Fight",
    title: "Fight",
    voice: "I need to protect myself.",
    color: "var(--response-fight)",
    whatIsHappening:
      "Your nervous system has read something as a threat and is getting you ready to push back against it. Energy moves outwards — into your voice, your muscles, your need to be heard or to regain control. It is not a character flaw; it is protection arriving faster than thought.",
    signs: [
      "Anger or sudden irritability",
      "Becoming defensive very quickly",
      "Wanting to argue or confront someone",
      "Feeling the need to be in control",
      "Raising your voice",
      "Feeling easily criticised or attacked",
      "Clenched jaw or fists",
      "Tight muscles",
      "Racing heart",
      "Feeling hot or flushed",
      "Difficulty calming down after conflict",
      "Feeling like you need to “win” or prove your point",
    ],
    body: [
      "Clenched jaw, fists or shoulders",
      "Heat rising in the chest, face or neck",
      "Racing heart and quick, shallow breath",
      "Muscles tight and ready, hard to sit still",
      "A charged feeling that lingers long after the moment passes",
    ],
    thoughts: [
      "“I have to defend myself.”",
      "“They are attacking me.”",
      "“I can't let them get away with this.”",
      "“If I lose control of this, something bad happens.”",
    ],
    behaviour: [
      "Replying sharply before you've had time to think",
      "Turning a conversation into a debate you need to win",
      "Taking charge of a situation so nothing can go wrong",
      "Replaying an argument afterwards, thinking of better answers",
    ],
    triggers: [
      "Criticism, blame or feeling misunderstood",
      "Feeling controlled or powerless",
      "Someone raising their voice",
      "Unfairness, or being interrupted or dismissed",
      "Reminders of times you had to defend yourself",
    ],
    rightNow: [
      "Let the energy move: shake out your hands, press your palms together hard for ten seconds, then release.",
      "Lengthen your out-breath — a longer exhale tells the body the fight is over.",
      "Step out of the room for two minutes before answering. Leaving is allowed.",
      "Cool water on your wrists or face can bring the heat down quickly.",
      "Name it silently: “my body is protecting me right now.”",
    ],
    practices: ["extended-exhale", "somatic", "tapping", "journal"],
  },
  {
    key: "flight",
    emoji: "🏃",
    label: "Flight",
    title: "Flight",
    voice: "I need to get away.",
    color: "var(--response-flight)",
    whatIsHappening:
      "Your nervous system is preparing to get you out of danger or discomfort. Sometimes that looks like physically leaving; often it looks like speed — racing thoughts, endless busyness, planning, perfecting. Movement feels safer than stillness.",
    signs: [
      "Anxiety or panic",
      "Restlessness",
      "Feeling unable to sit still",
      "Constantly keeping busy",
      "Overthinking",
      "Racing thoughts",
      "Avoiding difficult situations",
      "Wanting to leave or escape",
      "Perfectionism",
      "Overworking",
      "Rapid heartbeat",
      "Shallow or fast breathing",
      "Feeling that something bad is about to happen",
    ],
    body: [
      "Fast, shallow breathing high in the chest",
      "Rapid heartbeat, fluttering stomach",
      "Restless legs, tapping, pacing",
      "Tension around the eyes and jaw",
      "Exhaustion that arrives only when you finally stop",
    ],
    thoughts: [
      "“I need to get out of here.”",
      "“If I stop, everything falls apart.”",
      "“Something bad is about to happen.”",
      "“If I get it perfect, I'll be safe.”",
    ],
    behaviour: [
      "Filling every hour so there is no quiet space",
      "Leaving conversations, plans or places early",
      "Overworking, over-preparing, over-researching",
      "Scrolling or switching tasks to avoid a difficult feeling",
    ],
    triggers: [
      "Uncertainty or waiting for news",
      "Feeling trapped, or having no way out of a situation",
      "Conflict or the possibility of getting something wrong",
      "Too much to do and not enough time",
      "Reminders of situations you once had to escape",
    ],
    rightNow: [
      "Slow the breath before you slow the thoughts — try an even, four-count rhythm.",
      "Put both feet flat on the floor and name five things you can see.",
      "Give the energy somewhere to go: a short walk, stairs, stretching.",
      "Write the racing thoughts down so your mind doesn't have to keep holding them.",
      "Do one thing, not six. Choose the smallest one.",
    ],
    practices: ["box-breathing", "grounding", "panic-reset", "journal"],
  },
  {
    key: "freeze",
    emoji: "🧊",
    label: "Freeze",
    title: "Freeze",
    voice: "I can't move or respond.",
    color: "var(--response-freeze)",
    whatIsHappening:
      "When neither fighting nor escaping feels possible, the nervous system can hold everything still instead. Energy stays high inside while the outside goes quiet. This is not weakness or lack of willpower — it is an old, automatic way of staying safe.",
    signs: [
      "Feeling stuck or unable to act",
      "Mind suddenly going blank",
      "Difficulty speaking",
      "Difficulty making decisions",
      "Feeling numb",
      "Feeling disconnected from your surroundings",
      "Procrastinating even when you want to act",
      "Staring or zoning out",
      "Holding your breath",
      "Body feeling stiff or heavy",
      "Feeling unable to respond during conflict",
      "Knowing what you want to do but feeling unable to do it",
    ],
    body: [
      "Breath held or very shallow",
      "Stiffness through the neck, shoulders and chest",
      "Cold hands, or a numb, faraway feeling",
      "Sounds or colours seeming distant",
      "Heaviness, as if your limbs won't answer",
    ],
    thoughts: [
      "“I can't think of anything to say.”",
      "“I should be able to move and I can't.”",
      "“This isn't quite real.”",
      "“I'll deal with it later — I just can't now.”",
    ],
    behaviour: [
      "Going quiet in conflict, then thinking of your words hours later",
      "Staring at a task you genuinely want to do",
      "Avoiding decisions, even small ones",
      "Zoning out mid-conversation without meaning to",
    ],
    triggers: [
      "Conflict, raised voices or sudden pressure",
      "Feeling watched, judged or put on the spot",
      "Too many choices or demands at once",
      "Reminders of times when nothing you did helped",
    ],
    rightNow: [
      "Start very small: wiggle your toes, then your fingers. Movement can restart gently.",
      "Let the breath out slowly — the breath is often the first thing that freezes.",
      "Orient to the room: turn your head slowly and name what you see.",
      "Warmth helps — a blanket, a warm drink, hands on your upper arms.",
      "Nothing needs deciding right now. Coming back into your body is enough.",
    ],
    practices: ["grounding", "bilateral", "somatic", "regulation-compass"],
  },
  {
    key: "fawn",
    emoji: "🤝",
    label: "Fawn",
    title: "Fawn",
    voice: "I need to keep you happy so I can feel safe.",
    color: "var(--response-fawn)",
    whatIsHappening:
      "Sometimes safety came from keeping other people calm. The nervous system learns to reduce danger by pleasing, agreeing or caring for others, often before you notice you are doing it. Your own needs quietly move to the back of the queue.",
    signs: [
      "People-pleasing",
      "Difficulty saying no",
      "Automatically agreeing with others",
      "Excessive apologising",
      "Avoiding conflict",
      "Ignoring your own needs",
      "Changing yourself to fit other people",
      "Feeling responsible for other people's emotions",
      "Seeking reassurance or approval",
      "Difficulty setting boundaries",
      "Saying “I'm fine” when you're not",
      "Fear that someone will become angry if you disagree",
    ],
    body: [
      "A tight, anxious stomach when someone seems unhappy",
      "Smiling or nodding while feeling uneasy inside",
      "Shallow breath while you scan someone's face or tone",
      "Tension that only releases once they seem okay again",
    ],
    thoughts: [
      "“If they're upset with me, something bad will happen.”",
      "“It's easier if I just agree.”",
      "“My needs can wait.”",
      "“Am I being too much?”",
    ],
    behaviour: [
      "Saying yes when everything in you meant no",
      "Apologising for things that were never yours",
      "Managing other people's moods before your own",
      "Going quiet about what you actually wanted",
    ],
    triggers: [
      "Someone seeming disappointed, cold or annoyed",
      "Being asked for something directly",
      "Conflict, or the possibility of it",
      "Relationships where love once depended on keeping the peace",
    ],
    rightNow: [
      "Pause before answering: “Let me come back to you on that” is a complete sentence.",
      "Put a hand on your chest and ask, quietly, “what do I want here?”",
      "Notice the urge without obeying it — the urge is old, the moment is new.",
      "Write down the thing you didn't say. It still counts.",
      "One small boundary, kept, teaches the nervous system that disagreement can be survivable.",
    ],
    practices: ["inner-child", "journal", "grounding", "secure-within"],
  },
  {
    key: "shutdown",
    emoji: "🌙",
    label: "Shutdown",
    title: "Shutdown / Collapse",
    voice: "I don't have the energy to keep fighting.",
    color: "var(--response-shutdown)",
    whatIsHappening:
      "When overwhelm goes on too long, the nervous system can drop into a very low-energy protective state. Everything slows: feeling, motivation, movement. It can look like laziness or depression from the outside, but it is the body conserving what is left.",
    signs: [
      "Extreme tiredness",
      "Wanting to sleep",
      "Withdrawing from people",
      "Feeling emotionally numb",
      "Feeling heavy",
      "Very low motivation",
      "Difficulty concentrating",
      "Feeling disconnected",
      "Wanting to hide or be alone",
      "Feeling helpless",
      "Struggling with everyday tasks",
      "Feeling as though your body has simply “switched off”",
    ],
    body: [
      "Heaviness in the limbs, as if moving costs too much",
      "Slow, shallow breathing",
      "Flat or muted feelings",
      "Wanting darkness, quiet and sleep",
      "Everyday tasks feeling far away",
    ],
    thoughts: [
      "“What's the point.”",
      "“I just need everything to stop.”",
      "“I can't do this today.”",
      "“Nothing reaches me.”",
    ],
    behaviour: [
      "Cancelling plans and staying under the covers",
      "Letting messages go unanswered",
      "Basics — food, washing, tidying — slipping",
      "Going quiet with people who care about you",
    ],
    triggers: [
      "Long stretches of stress with no rest",
      "Grief, loss or repeated disappointment",
      "Feeling unseen or unsupported",
      "Reminders of times when nothing changed however hard you tried",
    ],
    rightNow: [
      "Aim for tiny: sit up, open a window, one sip of water.",
      "Gentle sensation helps — warmth, a soft blanket, a familiar sound.",
      "Light movement, not exercise: rolling your shoulders is enough.",
      "Tell one person, even in three words. Connection is regulating.",
      "If this stays for weeks, or you're thinking about not being here, please reach out to your doctor or a crisis line — you deserve real support.",
    ],
    practices: ["meditations", "acts-of-self-care", "grounding", "somatic"],
  },
];

export const TRAUMA_RESPONSE_DISCLAIMER =
  "These responses are automatic protective patterns of the nervous system. Experiencing them does not mean there is something wrong with you, and this tool is for self-reflection rather than diagnosis.";

export const TRAUMA_RESPONSE_NOT_FIXED =
  "You don't have one fixed response. You might fight in one situation, freeze in another and fawn in close relationships — and they often overlap or follow one another within minutes.";
